import React, { useState, useEffect, useCallback, useRef } from "react";
import { EyeIcon, EyeOffIcon } from "../components/icons";
import {
  login as apiLogin,
  verifyLoginOTP,
  getRememberMeCookie,
  getLoginRestriction,
} from "../services/authService";
import logoFull from "../assets/logo-full.png";
import logoFullWhite from "../assets/logo-full-white.png";
import authBg from "../assets/auth-bg.png";

/** Matches app header accent (--accent) */
const BRAND_RED = "#a51c1c";
const LOGIN_RESTRICTION_KEY = "ims_login_restriction";

function readStoredRestriction() {
  try {
    const raw = localStorage.getItem(LOGIN_RESTRICTION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.lockedUntil) return null;
    if (new Date(parsed.lockedUntil).getTime() <= Date.now()) {
      localStorage.removeItem(LOGIN_RESTRICTION_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function persistRestriction(payload) {
  try {
    localStorage.setItem(LOGIN_RESTRICTION_KEY, JSON.stringify(payload));
  } catch {
    // ignore quota / private mode failures
  }
}

function clearStoredRestriction() {
  try {
    localStorage.removeItem(LOGIN_RESTRICTION_KEY);
  } catch {
    // ignore
  }
}

const AuthPage = ({ onLogin }) => {
  const [isLoginMode] = useState(true);
  const [email, setEmail] = useState("");
  const [organizationCode, setOrganizationCode] = useState("");
  const [password, setPassword] = useState("");
  const [company, setCompany] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [success, setSuccess] = useState("");
  const [rememberMe, setRememberMe] = useState(() => getRememberMeCookie());
  const [restriction, setRestriction] = useState(null);
  const [restrictionChecked, setRestrictionChecked] = useState(false);
  const expiryTimerRef = useRef(null);

  const applyRestriction = useCallback((data, extras = {}) => {
    if (!data?.restricted || !data.lockedUntil) {
      clearStoredRestriction();
      setRestriction(null);
      return;
    }
    const next = {
      reason: data.reason || "rate_limit",
      lockedUntil: data.lockedUntil,
      lockoutMinutes: data.lockoutMinutes || 15,
      canResetPassword: !!data.canResetPassword,
      email: extras.email ?? "",
      organizationCode: extras.organizationCode ?? "",
    };
    persistRestriction(next);
    setRestriction(next);
    if (extras.email) setEmail(extras.email);
    if (extras.organizationCode != null) {
      setOrganizationCode(extras.organizationCode);
    }
  }, []);

  const clearRestriction = useCallback(() => {
    clearStoredRestriction();
    setRestriction(null);
  }, []);

  const syncRestrictionFromServer = useCallback(async () => {
    const stored = readStoredRestriction();
    const checkEmail = stored?.email || email || "";
    const checkOrg = stored?.organizationCode || organizationCode || "";

    const result = await getLoginRestriction(checkEmail, checkOrg);
    if (result.success && result.data?.restricted) {
      applyRestriction(result.data, {
        email: checkEmail,
        organizationCode: checkOrg,
      });
      return true;
    }

    // Server says clear — drop any stale local lockout UI state
    clearRestriction();
    return false;
  }, [applyRestriction, clearRestriction, email, organizationCode]);

  useEffect(() => {
    sessionStorage.removeItem("auth_failed");

    let cancelled = false;
    (async () => {
      const stored = readStoredRestriction();
      if (stored) {
        setRestriction(stored);
        if (stored.email) setEmail(stored.email);
        if (stored.organizationCode) {
          setOrganizationCode(stored.organizationCode);
        }
      }
      await syncRestrictionFromServer();
      if (!cancelled) setRestrictionChecked(true);
    })();

    return () => {
      cancelled = true;
    };
    // Intentionally once on mount — re-check is driven by expiry timer / login failures
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-restore login form when backend restriction expires
  useEffect(() => {
    if (expiryTimerRef.current) {
      clearTimeout(expiryTimerRef.current);
      expiryTimerRef.current = null;
    }
    if (!restriction?.lockedUntil) return undefined;

    const ms = new Date(restriction.lockedUntil).getTime() - Date.now();
    if (ms <= 0) {
      syncRestrictionFromServer();
      return undefined;
    }

    expiryTimerRef.current = setTimeout(() => {
      syncRestrictionFromServer();
    }, ms + 250);

    return () => {
      if (expiryTimerRef.current) {
        clearTimeout(expiryTimerRef.current);
        expiryTimerRef.current = null;
      }
    };
  }, [restriction?.lockedUntil, syncRestrictionFromServer]);

  const goToResetPassword = () => {
    const params = new URLSearchParams();
    const resetEmail = (restriction?.email || email).trim();
    const resetOrg = (restriction?.organizationCode || organizationCode).trim();
    if (resetEmail) params.set("email", resetEmail);
    if (resetOrg) params.set("organizationCode", resetOrg);
    const query = params.toString();
    window.history.pushState(
      {},
      "",
      query ? `/reset-password?${query}` : "/reset-password"
    );
    window.dispatchEvent(new PopStateEvent("popstate"));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (restriction) return;

    setError("");
    setSuccess("");
    setLoading(true);

    try {
      if (!email || !password) {
        setError("Email and password are required.");
        setLoading(false);
        return;
      }

      if (!organizationCode.trim()) {
        setError("Organization code is required.");
        setLoading(false);
        return;
      }

      if (!otpSent) {
        const result = await apiLogin(
          email.trim(),
          password,
          rememberMe,
          organizationCode.trim()
        );

        if (result.success) {
          clearRestriction();
          if (result.user) {
            const roleMap = {
              super_admin: "Super Admin",
              admin: "Admin",
              viewer: "Viewer",
            };
            const userRole = roleMap[result.user.role] || "Viewer";
            const tenantSlug = result.user.tenantSlug || "circuitflow";
            const userData = {
              ...result.user,
              name:
                `${result.user.firstName || ""} ${result.user.lastName || ""}`.trim() ||
                result.user.email,
            };
            onLogin(userRole, tenantSlug, userData);
            setPassword("");
          } else {
            setOtpSent(true);
            setSuccess("OTP sent to your email");
          }
        } else if (result.locked || result.rateLimited) {
          const lockedUntil =
            result.data?.lockedUntil ||
            new Date(
              Date.now() +
                (result.data?.retryAfterSeconds ||
                  (result.data?.lockoutMinutes || 15) * 60) *
                  1000
            ).toISOString();
          applyRestriction(
            {
              restricted: true,
              reason: result.locked ? "account_locked" : "rate_limit",
              lockedUntil,
              lockoutMinutes: result.data?.lockoutMinutes || 15,
              canResetPassword: !!result.data?.canResetPassword || !!result.locked,
            },
            {
              email: email.trim(),
              organizationCode: organizationCode.trim(),
            }
          );
          setError("");
        } else {
          setError(result.message || "Login failed");
        }
        setLoading(false);
        return;
      }

      if (!otp) {
        setError("OTP is required");
        setLoading(false);
        return;
      }

      const result = await verifyLoginOTP(email.trim(), otp, rememberMe);

      if (result.success) {
        const { user } = result;

        const roleMap = {
          super_admin: "Super Admin",
          admin: "Admin",
          viewer: "Viewer",
        };

        const userRole = roleMap[user.role] || "Viewer";
        const tenantSlug = user.tenantSlug || "circuitflow";

        const userData = {
          ...user,
          name:
            `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
            user.email,
        };

        onLogin(userRole, tenantSlug, userData);
        setOtp("");
        setOtpSent(false);
        setPassword("");
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const securityActive = !!restriction;
  const showAccountRecovery = restriction?.reason === "account_locked";

  const securityModal = securityActive && (
    <div
      className="bg-white w-full max-w-[440px] overflow-hidden shadow-lg"
      style={{
        borderRadius: "20px",
        boxShadow:
          "0 28px 80px rgba(0,0,0,0.35), 0 0 0 1px rgba(165,28,28,0.08)",
      }}
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="security-modal-title"
      aria-describedby="security-modal-description"
    >
      <div
        className="h-[4px]"
        style={{
          background: `linear-gradient(90deg, ${BRAND_RED}, #d44a4a, ${BRAND_RED})`,
        }}
      />
      <div className="px-8 pt-10 pb-8 text-center">
        <div
          className="mx-auto mb-5 flex h-[72px] w-[72px] items-center justify-center rounded-full"
          style={{
            background:
              "linear-gradient(145deg, #fef2f2 0%, #fee2e2 55%, #fecaca 100%)",
            boxShadow: "0 10px 28px rgba(165,28,28,0.18)",
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            className="h-9 w-9"
            aria-hidden="true"
          >
            <path
              d="M7 11V8a5 5 0 0 1 10 0v3"
              stroke={BRAND_RED}
              strokeWidth="1.8"
              strokeLinecap="round"
            />
            <rect
              x="5"
              y="11"
              width="14"
              height="10"
              rx="2.5"
              stroke={BRAND_RED}
              strokeWidth="1.8"
            />
            <circle cx="12" cy="16" r="1.4" fill={BRAND_RED} />
          </svg>
        </div>

        <p
          className="m-0 mb-2 text-[11px] font-semibold uppercase tracking-[0.16em]"
          style={{ color: BRAND_RED }}
        >
          Security notice
        </p>
        <h3
          id="security-modal-title"
          className="m-0 text-[22px] font-semibold leading-tight text-gray-900"
        >
          Too many login attempts
        </h3>
        <p
          id="security-modal-description"
          className="mt-3 mb-0 text-[14px] leading-relaxed text-gray-600"
        >
          {showAccountRecovery ? (
            <>
              Too many failed login attempts. Password login is locked for{" "}
              <span className="font-semibold text-gray-800">
                {restriction.lockoutMinutes} minutes
              </span>
              . Reset your password to regain access sooner, or wait for the
              lockout to expire.
            </>
          ) : (
            <>
              Too many failed login attempts. Please wait a few minutes and try
              again.
            </>
          )}
        </p>

        <div
          className="mt-6 flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-[13px] font-medium"
          style={{
            background: "#fafafa",
            border: "1px solid #eee",
            color: "#4b5563",
          }}
        >
          <span
            className="inline-flex h-2 w-2 rounded-full"
            style={{ background: BRAND_RED }}
          />
          {showAccountRecovery
            ? `Lockout ends after ${restriction.lockoutMinutes} minutes`
            : "Please wait a few minutes before trying again"}
        </div>

        <div className="mt-7 flex flex-col gap-3">
          {showAccountRecovery && (
            <button
              type="button"
              onClick={goToResetPassword}
              className="w-full rounded-xl border-0 py-3.5 text-[15px] font-semibold text-white cursor-pointer transition-opacity hover:opacity-95 focus:outline-none"
              style={{
                background: BRAND_RED,
                boxShadow: "0 10px 24px rgba(165,28,28,0.28)",
              }}
            >
              Reset Password
            </button>
          )}
          <p className="m-0 text-[12px] text-gray-500">
            The login form will return automatically when the restriction
            expires.
          </p>
        </div>
      </div>
    </div>
  );

  if (securityActive) {
    return (
      <div
        className="h-screen overflow-hidden flex justify-center items-center p-4 sm:p-6"
        style={{ background: BRAND_RED }}
      >
        {securityModal}
      </div>
    );
  }

  return (
    <div
      className="h-screen overflow-hidden flex justify-center items-center p-4 sm:p-6"
      style={{ background: BRAND_RED }}
    >
      <div className="bg-white w-full max-w-[1000px] max-h-full overflow-hidden flex flex-col lg:flex-row shadow-lg">
        {/* Left column: dark panel with logo and tagline */}
        <div
          className="hidden lg:flex lg:w-1/2 flex-col justify-center p-10 md:p-12 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${authBg})`,
            backgroundColor: "#1a1d21",
          }}
        >
          <img
            src={logoFullWhite}
            alt="JEBL Solutions"
            className="h-[60px] w-auto object-contain"
          />
        </div>

        {/* Right column: login form */}
        <div className="w-full lg:w-1/2 flex flex-col min-h-0 overflow-hidden">
          <div className="flex-1 px-8 py-8 md:px-10 md:py-10 flex flex-col justify-center">
            <div className="lg:hidden text-center mb-8">
              <img src={logoFull} alt="JEBL Solutions" className="h-12 mx-auto" />
            </div>

            <h4 className="text-[20px] font-semibold text-gray-900 mt-0 mb-1">
              JEBL Solutions Everywhere
            </h4>
            <h5 className="text-[17px] font-medium text-gray-700 m-0">
              Real-Time Analytics
            </h5>

            {error && (
              <p
                className="mt-4 text-sm text-center p-3 rounded border"
                style={{
                  background: "#fef2f2",
                  color: "#b91c1c",
                  borderColor: "#fecaca",
                }}
              >
                {error}
              </p>
            )}

            {success && (
              <p className="mt-4 text-sm text-center p-3 rounded border bg-green-100 text-green-700 border-green-200">
                {success}
              </p>
            )}

            <form onSubmit={handleSubmit} className="mt-8">
              {!isLoginMode && (
                <div className="mt-4">
                  <label className="block text-gray-800 font-semibold mb-1">
                    Company Name
                  </label>
                  <input
                    type="text"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    className="block w-full py-3 px-4 border text-sm focus:outline-none"
                    style={{ borderColor: "#ddd", borderWidth: "1.6px" }}
                  />
                </div>
              )}

              <div className="mt-4">
                <label className="block text-gray-800 font-semibold mb-1">
                  Organization Code
                </label>
                <input
                  type="text"
                  value={organizationCode}
                  onChange={(e) => setOrganizationCode(e.target.value)}
                  placeholder="Enter your organization code"
                  className="block w-full py-3 px-4 border text-sm focus:outline-none"
                  style={{ borderColor: "#ddd", borderWidth: "1.6px" }}
                  disabled={otpSent}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Provided by your administrator.
                </p>
              </div>

              <div className="mt-4">
                <label className="block text-gray-800 font-semibold mb-1">
                  E-mail Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email Address"
                  className="block w-full py-3 px-4 border text-sm focus:outline-none"
                  style={{ borderColor: "#ddd", borderWidth: "1.6px" }}
                  disabled={otpSent}
                />
              </div>

              <div className="mt-4">
                <label className="block text-gray-800 font-semibold mb-1">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter Password"
                    className="block w-full py-3 px-4 pr-11 border text-sm focus:outline-none"
                    style={{ borderColor: "#ddd", borderWidth: "1.6px" }}
                    disabled={otpSent}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700 focus:outline-none"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <EyeOffIcon className="w-5 h-5" />
                    ) : (
                      <EyeIcon className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              <label className="mt-4 flex items-center gap-2 cursor-pointer select-none text-sm text-gray-800">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 shrink-0 border-gray-300 rounded accent-[#a51c1c]"
                  style={{ accentColor: BRAND_RED }}
                />
                <span>remember me</span>
              </label>

              {otpSent && (
                <div className="mt-4">
                  <label className="block text-gray-800 font-semibold mb-1">
                    OTP
                  </label>
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    placeholder="Enter OTP"
                    className="block w-full py-3 px-4 border text-sm focus:outline-none"
                    style={{ borderColor: "#ddd", borderWidth: "1.6px" }}
                    required
                  />
                </div>
              )}
              {otpSent && (
                <div className="flex justify-end mt-2">
                  <button
                    type="button"
                    onClick={async () => {
                      setError("");
                      setSuccess("");

                      const result = await apiLogin(
                        email.trim(),
                        password,
                        rememberMe,
                        organizationCode.trim()
                      );
                      if (result.success) {
                        setSuccess("OTP resent successfully");
                      } else if (result.locked || result.rateLimited) {
                        const lockedUntil =
                          result.data?.lockedUntil ||
                          new Date(
                            Date.now() +
                              (result.data?.retryAfterSeconds || 15 * 60) * 1000
                          ).toISOString();
                        applyRestriction(
                          {
                            restricted: true,
                            reason: result.locked
                              ? "account_locked"
                              : "rate_limit",
                            lockedUntil,
                            lockoutMinutes: result.data?.lockoutMinutes || 15,
                            canResetPassword:
                              !!result.data?.canResetPassword || !!result.locked,
                          },
                          {
                            email: email.trim(),
                            organizationCode: organizationCode.trim(),
                          }
                        );
                      } else {
                        setError(result.message);
                      }
                    }}
                    className="text-sm hover:underline"
                    style={{ color: BRAND_RED }}
                  >
                    Resend OTP
                  </button>
                </div>
              )}

              {!otpSent && (
                <div className="text-right mt-2">
                  <button
                    type="button"
                    onClick={goToResetPassword}
                    className="font-medium text-sm hover:underline focus:outline-none"
                    style={{ color: BRAND_RED }}
                  >
                    Forgot Your Password?
                  </button>
                </div>
              )}
              <button
                type="submit"
                disabled={!restrictionChecked || loading || (otpSent && !otp)}
                className="w-full block py-3 px-4 text-center border-0 cursor-pointer mt-5 mb-3 text-base font-medium text-white disabled:opacity-70 disabled:cursor-not-allowed transition-opacity"
                style={{ background: BRAND_RED }}
              >
                {loading
                  ? "Processing..."
                  : otpSent
                  ? "Verify OTP & Login"
                  : "Login"}
              </button>
            </form>
          </div>

          <div className="text-center py-4 text-[#929292] text-sm">
            © 2026 All Rights Reserved
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
